import { useCallback } from "react";
import { Eye } from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import { useDocumentStore } from "../../stores/document";
import { useUIStore } from "../../stores/ui";
import { MarkdownPreview } from "./MarkdownPreview";
import { PdfPreview } from "./PdfPreview";

interface PreviewPanelProps {
  className?: string;
  onAIFix?: (errorSummary: string) => void;
}

export function PreviewPanel({ className, onAIFix }: PreviewPanelProps) {
  const currentDocument = useDocumentStore((s) => s.currentDocument);
  const compileResult = useDocumentStore((s) => s.compileResult);
  const errors = useDocumentStore((s) => s.errors);
  const isCompiling = useDocumentStore((s) => s.isCompiling);
  const isAutoCompiling = useDocumentStore((s) => s.isAutoCompiling);
  const autoCompile = useDocumentStore((s) => s.autoCompile);
  const compile = useDocumentStore((s) => s.compile);
  const setAutoCompile = useDocumentStore((s) => s.setAutoCompile);
  const openBottomPanelTab = useUIStore((s) => s.openBottomPanelTab);

  const handleAIFix = useCallback(() => {
    const errorMessages = errors
      .filter((e) => e.severity === "error")
      .map((e) => `${e.message}${e.line ? ` (line ${e.line})` : ""}`)
      .join("; ");
    onAIFix?.(
      `Fix these compilation errors in my document: ${errorMessages}`,
    );
  }, [errors, onAIFix]);

  const isLatex = currentDocument?.doc_type === "latex";
  const isMarkdown = currentDocument?.doc_type === "markdown";
  const isDocx = currentDocument?.doc_type === "docx";

  if (!currentDocument) {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center h-full gap-3 bg-[var(--bg-secondary)]",
          className,
        )}
      >
        <Eye size={36} weight="thin" className="text-[var(--text-muted)]" />
        <p className="text-xs text-[var(--text-muted)]">
          Open a document to see preview
        </p>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col h-full overflow-hidden bg-[var(--bg-primary)]", className)}>
      {isLatex && (
        <PdfPreview
          pdfUrl={compileResult?.pdf_url ?? null}
          isCompiling={isCompiling}
          isAutoCompiling={isAutoCompiling}
          autoCompile={autoCompile}
          compileErrors={errors}
          onToggleAutoCompile={() => setAutoCompile(!autoCompile)}
          onCompile={compile}
          onShowErrors={() => openBottomPanelTab("errors")}
          onAIFix={onAIFix ? handleAIFix : undefined}
        />
      )}

      {isMarkdown && (
        <MarkdownPreview content={currentDocument.content} />
      )}

      {isDocx && (
        <div className="flex flex-col items-center justify-center h-full gap-3 bg-[var(--bg-secondary)]">
          <Eye size={36} weight="thin" className="text-[var(--text-muted)]" />
          <p className="text-xs text-[var(--text-muted)]">
            WYSIWYG editor — preview is built into the editor
          </p>
        </div>
      )}
    </div>
  );
}
